(function qualucaBmiCoreFactory(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.QualucaBmiCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createQualucaBmiCore() {
  'use strict';

  const LIMITS = Object.freeze({
    weightMinKg: 35,
    weightMaxKg: 300,
    heightMinCm: 120,
    heightMaxCm: 220,
    underweightMax: 18.5,
    normalMax: 25,
    overweightMax: 30,
    healthyMin: 18.5,
    healthyMax: 24.9,
    gaugeMin: 14,
    gaugeMax: 40,
  });

  const PACE = Object.freeze({
    conservativeKgPerPeriod: 2,
    optimisticKgPerPeriod: 4,
    periodDays: 30,
  });

  const PROGRAM_DURATIONS = Object.freeze([45, 60, 90]);
  const MAINTENANCE_DURATION = 30;

  function parseLocaleNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
    const normalized = String(value == null ? '' : value)
      .trim()
      .replace(/\s+/g, '')
      .replace(',', '.');

    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return NaN;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  function validateInputs(weightValue, heightValue, ageConfirmed) {
    const weightKg = parseLocaleNumber(weightValue);
    const heightCm = parseLocaleNumber(heightValue);
    const errors = {};

    if (!String(weightValue == null ? '' : weightValue).trim()) {
      errors.weight = 'Introdu greutatea în kilograme.';
    } else if (!Number.isFinite(weightKg)) {
      errors.weight = 'Introdu o valoare numerică validă.';
    } else if (weightKg < LIMITS.weightMinKg || weightKg > LIMITS.weightMaxKg) {
      errors.weight = `Greutatea trebuie să fie între ${LIMITS.weightMinKg} și ${LIMITS.weightMaxKg} kg.`;
    }

    if (!String(heightValue == null ? '' : heightValue).trim()) {
      errors.height = 'Introdu înălțimea în centimetri.';
    } else if (!Number.isFinite(heightCm)) {
      errors.height = 'Introdu o valoare numerică validă.';
    } else if (heightCm < LIMITS.heightMinCm || heightCm > LIMITS.heightMaxCm) {
      errors.height = `Înălțimea trebuie să fie între ${LIMITS.heightMinCm} și ${LIMITS.heightMaxCm} cm.`;
    }

    if (!ageConfirmed) {
      errors.age = 'Confirmă că ai minimum 18 ani pentru a folosi calculatorul.';
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
      values: {weightKg, heightCm},
    };
  }

  function classifyBmi(bmi) {
    if (!Number.isFinite(bmi) || bmi <= 0) return null;
    if (bmi < LIMITS.underweightMax) return {id: 'underweight', label: 'Subponderal'};
    if (bmi < LIMITS.normalMax) return {id: 'normal', label: 'Greutate normală'};
    if (bmi < LIMITS.overweightMax) return {id: 'overweight', label: 'Supraponderal'};
    return {id: 'obesity', label: 'Obezitate'};
  }

  function estimateDays(kgAboveHealthyRange) {
    if (!Number.isFinite(kgAboveHealthyRange) || kgAboveHealthyRange <= 0) {
      return {conservative: 0, optimistic: 0};
    }

    return {
      conservative: Math.ceil((kgAboveHealthyRange / PACE.conservativeKgPerPeriod) * PACE.periodDays),
      optimistic: Math.ceil((kgAboveHealthyRange / PACE.optimisticKgPerPeriod) * PACE.periodDays),
    };
  }

  function selectProgram(estimatedDays) {
    if (!Number.isFinite(estimatedDays) || estimatedDays <= 0) return null;
    const durationDays = PROGRAM_DURATIONS.find((duration) => duration >= estimatedDays) || 90;
    return {
      durationDays,
      isInitialStage: estimatedDays > 90,
    };
  }

  function gaugePosition(bmi) {
    if (!Number.isFinite(bmi)) return 0;
    const clamped = Math.min(LIMITS.gaugeMax, Math.max(LIMITS.gaugeMin, bmi));
    return ((clamped - LIMITS.gaugeMin) / (LIMITS.gaugeMax - LIMITS.gaugeMin)) * 100;
  }

  function calculateMetrics(weightKg, heightCm) {
    const heightM = heightCm / 100;
    const heightSquared = heightM * heightM;
    const bmi = weightKg / heightSquared;
    const healthyMinKg = LIMITS.healthyMin * heightSquared;
    const healthyMaxKg = LIMITS.healthyMax * heightSquared;
    const kgAboveHealthyRange = Math.max(0, weightKg - healthyMaxKg);
    const kgBelowHealthyRange = Math.max(0, healthyMinKg - weightKg);
    const category = classifyBmi(bmi);
    const estimatedDays = estimateDays(kgAboveHealthyRange);
    let recommendation = null;
    if (category?.id === 'normal') {
      recommendation = {
        durationDays: MAINTENANCE_DURATION,
        isInitialStage: false,
        isMaintenance: true,
      };
    } else if (category && (category.id === 'overweight' || category.id === 'obesity')) {
      recommendation = {
        ...selectProgram(estimatedDays.conservative),
        isMaintenance: false,
      };
    }

    return {
      weightKg,
      heightCm,
      heightM,
      bmi,
      category,
      healthyMinKg,
      healthyMaxKg,
      kgAboveHealthyRange,
      kgBelowHealthyRange,
      estimatedDays,
      recommendation,
      gaugePosition: gaugePosition(bmi),
    };
  }

  function displayDays(days) {
    if (!Number.isFinite(days) || days <= 0) return 0;
    return days > 60 ? Math.max(1, Math.round(days / 5) * 5) : Math.round(days);
  }

  return Object.freeze({
    LIMITS,
    PACE,
    PROGRAM_DURATIONS,
    MAINTENANCE_DURATION,
    parseLocaleNumber,
    validateInputs,
    classifyBmi,
    estimateDays,
    selectProgram,
    gaugePosition,
    calculateMetrics,
    displayDays,
  });
});
